import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// order-entry

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OESettingsService } from "./oe-settings.service";
import { OENDisplayState } from "../../data/enums/oe-display-state.enum";
import { OENDialogType } from "../../data/enums/oe-dialog-type.enum";
import { OEModelGetterService } from "../model/oe-model-getter.service";
import { TranslateService } from "@ngx-translate/core";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";
import { OECloserService } from "../oe-closer.service";
import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";

@Injectable()
export class OEDialogService {
  /**********************************************************************/
  /* DATA  */
  private activeDialogs: Object = new Object();

  // dialogs
  private orderDialogSource = new Subject<BfxDialogData>();
  public dialogCtrl$ = this.orderDialogSource.asObservable();

  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    private translate: TranslateService,
    private settings: OESettingsService,
    private getter: OEModelGetterService,
    private closer: OECloserService,
    private _layoutService: GoldenLayoutService,
  ) {}

  /**********************************************************************/
  /* DIALOGS - API */

  /**
   * return false if dialog is shown, return true if dialog is not shown
   */
  /// actionAbortAmendMarkupWithDialog
  public abortAmendMarkup(refId: string): boolean {
    if (this.getter.has(refId)) {
      //let orderState = this.getter.getOrderStateForModifyIssuer(refId);
      let issuerRefId = this.getter.getModifyIssuerRefId(refId);
      let displayState = this.getter.getDisplayState(issuerRefId);

      issuerRefId = issuerRefId != null && issuerRefId.length > 0 ? issuerRefId : refId;

      if (
        this.getter.isOperationModeAmendMarkup(refId) &&
        displayState === OENDisplayState.SHOW_AMEND_MARKUP
      ) {
        this.showDialog(
          refId,
          OENDialogType.AMEND_MARKUP_ABORT,
          this.closer.closeAndUnlockAmendMarkup.bind(this.closer, issuerRefId),
          this.closer.negativeClose.bind(this.closer, issuerRefId),
        );
        return false;
      }
    }
    return true;
  }
  /**
   * return false if dialog is shown, return true if dialog is not shown
   */
  //actionAbortModifyWithDialog
  public abortModify(refId: string): boolean {
    if (this.getter.has(refId)) {
      let orderState = this.getter.getOrderStateForModifyIssuer(refId);
      let issuerRefId = this.getter.getModifyIssuerRefId(refId);
      issuerRefId = issuerRefId != null && issuerRefId.length > 0 ? issuerRefId : refId;

      if (this.getter.lockedByAnotherUser(refId)) {
        // We are only monitoring the order so can close without prompt
        return true;
      }

      if (this.getter.isOrderDisplayStateTerminal(refId)) {
        // Regardless of order state (and, for REJECT, we wil have unsubscribed to rollbacks)
        return true;
      }
      if (
        orderState === CubiqTS.OrderState.IN_MODIFICATION &&
        // OMS-33463: migrate on panic UNLOCKs modification & kill OE window
        // msg displayed & no abort generated so no warning required
        this.getter.isLockedForModification(refId)
      ) {
        this.showDialog(
          refId,
          OENDialogType.MODIFICATION_ABORT,
          this.closer.closeAndUnlockModify.bind(this.closer, issuerRefId),
          this.closer.negativeClose.bind(this.closer, issuerRefId),
        );
        return false;
      } else if (orderState === CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED) {
        this.showDialog(
          refId,
          OENDialogType.MODIFICATION_ABORT,
          this.closer.close.bind(this.closer, issuerRefId),
        ); // close() will trigger an abort
        return false;
      } else if (orderState === CubiqTS.OrderState.ACCEPTANCE_NEEDED) {
        this.showDialog(
          refId,
          OENDialogType.MODIFICATION_CLOSE,
          this.closer.close.bind(this.closer, issuerRefId),
        );
        return false;
      } else if (
        orderState === CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED ||
        orderState === CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED
      ) {
        this.showDialog(
          refId,
          OENDialogType.MODIFICATION_CLOSE_EXT_ORDER,
          this.closer.close.bind(this.closer, issuerRefId),
          this.closer.negativeClose.bind(this.closer, issuerRefId),
        );
        return false;
      } else if (orderState === CubiqTS.OrderState.DI_NEEDED) {
        this.showDialog(
          refId,
          OENDialogType.MODIFICATION_CLOSE,
          this.closer.close.bind(this.closer, issuerRefId),
        );
        return false;
      }
    }
    return true;
  }

  /**
   *
   */
  // closeNewEntry -> actionCloseNewOrderEntryWithDialog
  public closeNewEntry(refId: string): boolean {
    if (this.getter.has(refId)) {
      const acceptanceExternal = [OENDisplayState.SHOW_ACCEPTANCE_EXTERNAL];
      const acceptanceState = [OENDisplayState.SHOW_ACCEPTANCE_ALLOWED];
      const diStates = [
        OENDisplayState.SHOW_PROCESSING,
        OENDisplayState.SHOW_APPROVAL_ALLOWED,
        OENDisplayState.SHOW_APPROVAL_INTERNAL,
        OENDisplayState.SHOW_APPROVAL_EXTERNAL,
      ];
      const orderState = this.getter.getDisplayState(refId);

      if (
        orderState === OENDisplayState.SHOW_PROCESSING &&
        this.getter.getOrderIdForRefIdOrRoot(refId) == null
      ) {
        this.showNotAllowedDialog(refId, OENDialogType.ORDER_ENTRY_ABORT_NOT_ALLOWED);
        return false;
      } else if (acceptanceExternal.includes(orderState)) {
        this.showDialog(
          refId,
          OENDialogType.ORDER_ENTRY_CLOSE_EXT_ORDER,
          this.closer.close.bind(this.closer, refId),
          this.closer.negativeClose.bind(this.closer, refId),
        );
        return false;
      } else if (acceptanceState.includes(orderState)) {
        this.showDialog(
          refId,
          OENDialogType.ORDER_ENTRY_CLOSE,
          this.closer.close.bind(this.closer, refId),
        );
        return false;
      } else if (diStates.includes(orderState)) {
        this.showDialog(
          refId,
          OENDialogType.ORDER_ENTRY_CLOSE,
          this.closer.close.bind(this.closer, refId),
        );
        return false;
      }
    }
    return true;
  }

  /**********************************************************************/
  /* DIALOGS - DEFINITION AND INVOCATION */

  /* CMD - Closing the Order Entry screen */
  public showDialog(
    refId: string,
    dialogType: OENDialogType,
    _callback: Function,
    _negCallback?: Function,
  ): void {
    let message: string;
    let subMessage: string;
    let title: string;
    let error: boolean = false;
    switch (dialogType) {
      case OENDialogType.ORDER_ENTRY_CLOSE:
      case OENDialogType.MODIFICATION_CLOSE:
        message = this.translate.instant("order_entry.dialog.close.message");
        subMessage = this.translate.instant("order_entry.dialog.close.subMessage");
        title = "order_entry.dialog.close.title";
        break;
      case OENDialogType.ORDER_ENTRY_CLOSE_EXT_ORDER:
      case OENDialogType.MODIFICATION_CLOSE_EXT_ORDER:
        message = this.translate.instant("order_entry.dialog.close.externalOrderWarning.message");
        subMessage = this.translate.instant(
          "order_entry.dialog.close.externalOrderWarning.subMessage",
        );
        title = "order_entry.dialog.close.externalOrderWarning.title";
        break;
      case OENDialogType.ORDER_ENTRY_FORCE_EXT_ORDER:
        message = this.translate.instant("order_entry.dialog.force.externalOrderWarning.message");
        subMessage = this.translate.instant(
          "order_entry.dialog.force.externalOrderWarning.subMessage",
        );
        title = "order_entry.dialog.force.externalOrderWarning.title";
        break;
      case OENDialogType.MODIFICATION_ABORT:
        message = this.translate.instant("modify.dialog.abortOnClose.message");
        subMessage = this.translate.instant("modify.dialog.abortOnClose.subMessage");
        title = "modify.dialog.abortOnClose.title";
        break;
      case OENDialogType.AMEND_MARKUP_ABORT:
        message = this.translate.instant("amend_markup.dialog.abortOnClose.message");
        subMessage = this.translate.instant("amend_markup.dialog.abortOnClose.subMessage");
        title = "amend_markup.dialog.abortOnClose.title";
        break;
      default:
        logger.out.error(
          `ERROR: _oeCoordinator.showDialog(): Invalid dialog type requested! type= ${dialogType}`,
        );
        error = true;
        break;
    }
    if (!error) {
      let payload: any = {
        title: title,
        messages: [message],
        subMessage: subMessage,
        callback: _callback,
        callbackNegative: _negCallback,
      };
      this.orderDialogSource.next(
        new BfxDialogData(
          BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
          null,
          event,
          payload,
          this._layoutService["order_entry_dialog"],
        ),
      );
    }
  }

  public showNotAllowedDialog(refId: string, dialogType: OENDialogType): void {
    let rootRefId: string = this.getter.getRootIdForNodeWithRefId(refId);
    let error: boolean = false;
    //
    let message: string;
    let title: string;
    let dialogId: string;

    switch (dialogType) {
      case OENDialogType.ORDER_ENTRY_ABORT_NOT_ALLOWED:
        message = this.translate.instant("order_entry.dialog.abortNotAllowed.message");
        title = "order_entry.dialog.abortNotAllowed.title";
        dialogId = "cgok-" + rootRefId;
        break;
      default:
        logger.out.error(
          `ERROR: _oeCoordinator.showDialog(): Invalid dialog type requested! type= ${dialogType}`,
        );
        error = true;
        return;
    }
    if (!error) {
      let payload: any = {
        title: title,
        messages: [message],
        callback: () => {
          this.removeDialog(rootRefId);
        },
        callbackNegative: () => {
          this.removeDialog(rootRefId);
        },
      };
      this.orderDialogSource.next(
        new BfxDialogData(BfxDialogCommand.DIALOG_GENERIC_OK, dialogId, event, payload),
      );
      this.addDialog(rootRefId, dialogId);
    }
  }

  /**********************************************************************/
  /* DIALOG - MANAGEMENT */

  // CHANGE: addActiveDialog
  private addDialog(refId: string, dialogId: string): void {
    if (
      typeof refId === "string" &&
      refId.length > 0 &&
      typeof dialogId === "string" &&
      dialogId.length > 0
    ) {
      if (this.activeDialogs.hasOwnProperty(refId)) {
        // REMOVE
        this.removeDialog(refId);
      }
      if (!this.activeDialogs.hasOwnProperty(refId)) {
        // ADD
        this.activeDialogs = { ...this.activeDialogs, [refId]: dialogId };
      }
    }
  }

  private getAndRemoveDialog(refId: string): string | null {
    let dialogId = this.getDialogId(refId);
    if (dialogId != null) {
      this.removeDialog(refId);
    }
    return dialogId;
  }

  private removeDialog(refId: string): void {
    if (this.activeDialogs.hasOwnProperty(refId)) {
      delete this.activeDialogs[refId];
    }
  }

  // change: getActiveDialogId
  private getDialogId(refId: string): string {
    if (this.activeDialogs.hasOwnProperty(refId)) {
      return this.activeDialogs[refId];
    }
    return null;
  }
  // change: closeActiveDialogsForRefId
  public closeDialogsRefs(refId: string): void {
    let dialogId: string = this.getAndRemoveDialog(refId);
    if (dialogId != null) {
      this.orderDialogSource.next(
        new BfxDialogData(BfxDialogCommand.DIALOG_CLOSE, dialogId, null, null),
      );
    } else {
      // error handling
      let rootRefId: string = this.getter.getRootIdForNodeWithRefId(refId);
      dialogId = this.getAndRemoveDialog(rootRefId);
      if (dialogId != null) {
        this.orderDialogSource.next(
          new BfxDialogData(BfxDialogCommand.DIALOG_CLOSE, dialogId, null, null),
        );
      }
    }
  }
}
